import { useEffect, useState, type FormEvent } from "react";
import {
  billingApi,
  type BillingEntitlementDto,
  type BillingPlanDto,
} from "../api.js";
import { Button, Card, DetailList, EmptyState, ErrorBanner, Field, Money, PageHeader, Select, Spinner } from "../components/ui.js";

export function BillingPage() {
  const [plans, setPlans] = useState<BillingPlanDto[] | null>(null);
  const [entitlement, setEntitlement] = useState<BillingEntitlementDto | null>(null);
  const [error, setError] = useState<string | null>(null);
  const checkoutResult = new URLSearchParams(window.location.search).get("checkout");

  useEffect(() => {
    void (async () => {
      const [p, e] = await Promise.all([billingApi.plans(), billingApi.entitlement()]);
      if (!p.ok) setError(p.error?.errorCode ?? "Billing unavailable");
      else setPlans(p.data ?? []);
      if (!e.ok) setError(e.error?.errorCode ?? "Subscription unavailable");
      else setEntitlement(e.data ?? null);
    })();
  }, []);

  if (error) return <ErrorBanner message={error} />;
  if (plans === null) return <Spinner />;

  return (
    <div className="page-stack">
      <PageHeader
        eyebrow="Account"
        title="Billing"
        description="Review your subscription, choose a plan, and manage billing securely through Stripe."
      />
      {checkoutResult === "success" && (
        <div className="billing-notice" role="status">
          Stripe has returned you to Nettstay. Access updates after the signed billing event is confirmed.
        </div>
      )}
      {checkoutResult === "cancelled" && (
        <div className="billing-notice billing-notice-muted" role="status">
          Checkout was cancelled. Your current access has not changed.
        </div>
      )}
      <Card title="Current plan" description="The access currently attached to your Nettstay account.">
        {entitlement ? (
          <>
            <DetailList data={{
              "Plan": entitlement.planName,
              "Status": formatSubscriptionState(entitlement.state),
              "Price": entitlement.priceMonthly === 0
                ? "Free"
                : `${entitlement.currency} ${entitlement.priceMonthly.toFixed(2)} / ${entitlement.billingInterval}`,
              "Paid access": entitlement.isActive ? "Active" : "Not active",
              "Renews / ends": entitlement.currentPeriodEnd
                ? new Date(entitlement.currentPeriodEnd).toLocaleDateString("en-NZ")
                : "Not scheduled",
            }} />
            {entitlement.cancelAtPeriodEnd && (
              <p className="billing-state-warning">Cancellation is scheduled for the end of the current billing period.</p>
            )}
            {["pastdue", "unpaid"].includes(normalizeSubscriptionState(entitlement.state)) && (
              <p className="billing-state-warning">Payment needs attention. Use Manage billing to update your payment method.</p>
            )}
            {entitlement.canManageBilling && <ManageBillingButton />}
          </>
        ) : (
          <EmptyState title="No subscription details" message="Billing details are temporarily unavailable." />
        )}
      </Card>
      <Card title="Available plans" description="Plans returned by the current billing service.">
        {plans.length === 0 ? <EmptyState title="No plans available" message="Billing plans are not available right now. Please try again later." /> : (
          <div className="plan-grid">
            {plans.map((plan) => (
              <article className="plan-card" key={plan.id}>
                <div>
                  <h3>{plan.name}</h3>
                  <p className="plan-price">
                    <Money amount={plan.priceMonthly} currency={plan.currency} />
                    <span>/ {plan.billingInterval}</span>
                  </p>
                </div>
                <ul className="plan-features">
                  <li>{plan.maxProperties < 0 ? "Unlimited properties" : `${plan.maxProperties} ${plan.maxProperties === 1 ? "property" : "properties"}`}</li>
                  <li>{plan.maxExpensesPerYear < 0 ? "Unlimited expense records" : `${plan.maxExpensesPerYear} expense records per year`}</li>
                </ul>
              </article>
            ))}
          </div>
        )}
        {plans.some((plan) => plan.isCheckoutAvailable) && (
          <CheckoutForm plans={plans.filter((plan) => plan.isCheckoutAvailable)} />
        )}
      </Card>
    </div>
  );
}

function CheckoutForm({ plans }: { plans: BillingPlanDto[] }) {
  const [planCode, setPlanCode] = useState(plans[0]?.code ?? "");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault(); setError(null); setBusy(true);
    const res = await billingApi.checkout({
      planCode,
      successUrl: `${window.location.origin}/billing?checkout=success`,
      cancelUrl: `${window.location.origin}/billing?checkout=cancelled`,
    });
    setBusy(false);
    if (!res.ok) { setError(res.error?.errorCode ?? "Checkout failed"); return; }
    if (!res.data?.checkoutUrl) { setError("Checkout failed"); return; }
    window.location.assign(res.data.checkoutUrl);
  };

  return (
    <form onSubmit={onSubmit} className="form-inline">
      {error && <ErrorBanner message={error} />}
      <Field label="Choose a plan">
        <Select required value={planCode} onChange={(e) => setPlanCode(e.target.value)}>
          {plans.map((plan) => <option key={plan.id} value={plan.code}>{plan.name}</option>)}
        </Select>
      </Field>
      <Button type="submit" disabled={busy}>{busy ? "Preparing…" : "Continue to secure checkout"}</Button>
    </form>
  );
}

function ManageBillingButton() {
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const openPortal = async () => {
    setError(null);
    setBusy(true);
    const result = await billingApi.portal({
      returnUrl: `${window.location.origin}/billing`,
    });
    setBusy(false);
    if (!result.ok || !result.data?.portalUrl) {
      setError(result.error?.errorCode ?? "Billing portal unavailable");
      return;
    }
    window.location.assign(result.data.portalUrl);
  };

  return (
    <div className="billing-actions">
      {error && <ErrorBanner message={error} />}
      <Button type="button" variant="secondary" disabled={busy} onClick={() => void openPortal()}>
        {busy ? "Opening…" : "Manage billing"}
      </Button>
    </div>
  );
}

function formatSubscriptionState(state: string): string {
  return state
    .replace(/([a-z])([A-Z])/g, "$1_$2")
    .split("_")
    .map((part) => part ? part[0]!.toUpperCase() + part.slice(1) : part)
    .join(" ");
}

function normalizeSubscriptionState(state: string): string {
  return state.replaceAll("_", "").toLowerCase();
}
